import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import { planService } from "../services/planService";

const inputCls =
  "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary";

const empty = {
  name: "",
  audience: "shop",
  price_monthly: 0,
  price_semi_annual: 0,
  price_annual: 0,
  products_limit: "",
  lives_limit: "",
  description: "",
  is_active: true,
  sort_order: 0,
};

const Field = ({ label, span2, children }) => (
  <div className={span2 ? "col-span-2" : ""}>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const PlanList = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {} (new) | plan (edit)
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["plans"],
    queryFn: () => planService.getPlans(),
  });

  const saveMut = useMutation({
    mutationFn: (payload) =>
      editing?.id
        ? planService.updatePlan(editing.id, payload)
        : planService.createPlan(payload),
    onSuccess: () => {
      toast.success("Plan enregistré");
      qc.invalidateQueries({ queryKey: ["plans"] });
      closeForm();
    },
    onError: () => toast.error("Erreur lors de l'enregistrement"),
  });

  const delMut = useMutation({
    mutationFn: (id) => planService.deletePlan(id),
    onSuccess: () => {
      toast.success("Plan supprimé");
      qc.invalidateQueries({ queryKey: ["plans"] });
      setToDelete(null);
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return <div className="text-red-500">Erreur lors du chargement des plans</div>;

  const plans = data?.data || [];
  const shopPlans = plans.filter((p) => p.audience === "shop");
  const servicePlans = plans.filter((p) => p.audience === "service");

  const openNew = (audience) => {
    setEditing({});
    setForm({ ...empty, audience });
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      products_limit: p.products_limit ?? "",
      lives_limit: p.lives_limit ?? "",
    });
  };
  const closeForm = () => {
    setEditing(null);
    setForm(empty);
  };

  const submit = (e) => {
    e.preventDefault();
    saveMut.mutate({
      name: form.name,
      audience: form.audience,
      price_monthly: Number(form.price_monthly) || 0,
      price_semi_annual: Number(form.price_semi_annual) || 0,
      price_annual: Number(form.price_annual) || 0,
      products_limit: form.products_limit === "" ? null : Number(form.products_limit),
      lives_limit: form.lives_limit === "" ? null : Number(form.lives_limit),
      description: form.description || "",
      is_active: !!form.is_active,
      sort_order: Number(form.sort_order) || 0,
    });
  };

  const fmt = (n) => Number(n).toLocaleString("fr-FR") + " F";
  const lim = (v) => (v === null || v === undefined ? "∞" : v);

  const table = (title, list, audience) => (
    <div className="bg-white shadow-md rounded-lg mb-6">
      <div className="p-4 border-b flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        <button
          onClick={() => openNew(audience)}
          className="flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-md text-sm"
        >
          <PlusIcon className="h-4 w-4" /> Nouveau plan
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              {["Nom", "Mensuel", "6 mois", "1 an", "Produits", "Lives/mois", "Actif", "Actions"].map(
                (h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {list.length ? (
              list.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 text-sm font-semibold">{p.name}</td>
                  <td className="px-4 py-3 text-sm">{fmt(p.price_monthly)}</td>
                  <td className="px-4 py-3 text-sm">{fmt(p.price_semi_annual)}</td>
                  <td className="px-4 py-3 text-sm">{fmt(p.price_annual)}</td>
                  <td className="px-4 py-3 text-sm">{lim(p.products_limit)}</td>
                  <td className="px-4 py-3 text-sm">{lim(p.lives_limit)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                        p.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {p.is_active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        className="p-2 hover:bg-gray-100 rounded"
                        title="Modifier"
                      >
                        <PencilSquareIcon className="h-6 w-6 text-blue-600" />
                      </button>
                      <button
                        onClick={() => setToDelete(p)}
                        className="p-2 hover:bg-red-100 rounded"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-6 w-6 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="px-4 py-6 text-center text-sm text-gray-500">
                  Aucun plan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      {table("Plans Boutiques", shopPlans, "shop")}
      {table("Plans Services", servicePlans, "service")}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={submit}
            className="bg-white rounded-lg w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto"
          >
            <h3 className="text-lg font-semibold mb-4">
              {editing?.id ? "Modifier le plan" : "Nouveau plan"}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom" span2>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Audience">
                <select
                  value={form.audience}
                  onChange={(e) => setForm({ ...form, audience: e.target.value })}
                  className={inputCls}
                >
                  <option value="shop">Boutique</option>
                  <option value="service">Service</option>
                </select>
              </Field>
              <Field label="Ordre d'affichage">
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Prix mensuel (F)">
                <input
                  type="number"
                  value={form.price_monthly}
                  onChange={(e) => setForm({ ...form, price_monthly: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Prix 6 mois (F)">
                <input
                  type="number"
                  value={form.price_semi_annual}
                  onChange={(e) => setForm({ ...form, price_semi_annual: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Prix annuel (F)">
                <input
                  type="number"
                  value={form.price_annual}
                  onChange={(e) => setForm({ ...form, price_annual: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Produits (vide = illimité)">
                <input
                  type="number"
                  value={form.products_limit}
                  onChange={(e) => setForm({ ...form, products_limit: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Lives / mois (vide = illimité)">
                <input
                  type="number"
                  value={form.lives_limit}
                  onChange={(e) => setForm({ ...form, lives_limit: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Description" span2>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={inputCls}
                  rows={2}
                />
              </Field>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Plan actif
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={closeForm}
                className="px-4 py-2 rounded-md border"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saveMut.isPending}
                className="px-4 py-2 rounded-md bg-primary text-white"
              >
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}

      <DeleteConfirmation
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && delMut.mutate(toDelete.id)}
        title="Supprimer le plan"
        message={`Supprimer le plan « ${toDelete?.name} » ?`}
        confirmButtonText="Supprimer"
        confirmButtonColor="danger"
      />
    </div>
  );
};

export default PlanList;
